import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Heading,
  VStack,
  HStack,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Card,
  CardBody,
  Text,
  Badge,
  useColorModeValue,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Textarea,
  useToast,
  InputGroup,
  InputLeftElement,
  Spinner,
} from '@chakra-ui/react';
import { api } from '../services/api';

interface Subject {
  id: number;
  name: string;
  description?: string;
  seriesId: number;
  classId: number;
  series?: {
    id: number;
    name: string;
    type?: string;
  };
  class?: {
    id: number;
    name: string;
  };
}

const SubjectsPage = () => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [series, setSeries] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    seriesId: '',
    classId: '',
    name: '',
    description: '',
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');

  // Filter subjects based on search term
  const filteredSubjects = useMemo(() => {
    if (!searchTerm) return subjects;
    const searchTermLower = searchTerm.toLowerCase();
    
    return subjects.filter((subject) =>
      subject.name.toLowerCase().includes(searchTermLower) ||
      (subject.description && subject.description.toLowerCase().includes(searchTermLower)) ||
      (subject.series?.name && subject.series.name.toLowerCase().includes(searchTermLower)) ||
      (subject.series?.type && subject.series.type.toLowerCase().includes(searchTermLower)) ||
      (subject.class?.name && subject.class.name.toLowerCase().includes(searchTermLower))
    );
  }, [subjects, searchTerm]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [subjectsData, seriesData, classesData] = await Promise.all([
        api.getSubjects(),
        api.getSeries(),
        api.getClasses()
      ]);
      setSubjects(subjectsData);
      setSeries(seriesData);
      setClasses(classesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    if (!formData.seriesId || !formData.classId || !formData.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      setLoading(true);
      const subjectData = {
        ...formData,
        name: formData.name.trim(),
        description: formData.description.trim(),
        seriesId: Number(formData.seriesId),
        classId: Number(formData.classId)
      };

      if (editingSubject) {
        await api.updateSubject(editingSubject.id, subjectData);
        toast({
          title: 'Success',
          description: 'Subject updated successfully',
          status: 'success',
          duration: 3000,
        });
      } else {
        await api.createSubject(subjectData);
        toast({
          title: 'Success',
          description: 'Subject created successfully',
          status: 'success',
          duration: 3000,
        });
      }
      
      resetForm();
      onClose();
      loadData();
    } catch (error: any) {
      console.error('Error saving subject:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save subject',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this subject?')) {
      try {
        setLoading(true);
        await api.deleteSubject(id);
        toast({
          title: 'Success',
          description: 'Subject deleted successfully',
          status: 'success',
          duration: 3000,
        });
        loadData();
      } catch (error) {
        console.error('Error deleting subject:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete subject',
          status: 'error',
          duration: 3000,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (subject: Subject) => {
    setEditingSubject(subject);
    setFormData({
      seriesId: subject.seriesId.toString(),
      classId: subject.classId.toString(),
      name: subject.name,
      description: subject.description || '',
    });
    onOpen();
  };

  const resetForm = () => {
    setFormData({ seriesId: '', classId: '', name: '', description: '' });
    setEditingSubject(null);
  };

  useEffect(() => {
    loadData();
  }, []); // Remove loadData from dependencies since it's now memoized

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between">
        <Heading size="xl" color="gray.700">
          📝 Subjects Management
        </Heading>
        <Button colorScheme="green" onClick={() => { resetForm(); onOpen(); }}>
          ➕ Add Subject
        </Button>
      </HStack>

      {/* Search Bar */}
      <Card bg={cardBg} shadow="sm">
        <CardBody>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Text>🔍</Text>
            </InputLeftElement>
            <Input
              placeholder="Search subjects by name, description, series, class..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              bg="white"
            />
          </InputGroup>
          {searchTerm && (
            <Text fontSize="sm" color="gray.500" mt={2}>
              Found {filteredSubjects.length} of {subjects.length} subjects
            </Text>
          )}
        </CardBody>
      </Card>

      {/* Subjects Table */}
      <Card bg={cardBg} shadow="md">
        <CardBody>
          {loading ? (
            <VStack py={8}>
              <Spinner size="xl" />
              <Text>Loading...</Text>
            </VStack>
          ) : filteredSubjects.length === 0 ? (
            searchTerm ? (
              <Alert status="info">
                <AlertIcon />
                No subjects found matching "{searchTerm}". Try a different search term.
              </Alert>
            ) : (
              <Alert status="info">
                <AlertIcon />
                No subjects found. Create your first subject!
              </Alert>
            )
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Description</Th>
                  <Th>Series</Th>
                  <Th>Type</Th>
                  <Th>Class</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredSubjects.map((subject) => (
                  <Tr key={subject.id}>
                    <Td fontWeight="medium">{subject.name}</Td>
                    <Td maxW="200px" isTruncated>
                      {subject.description || 'No description'}
                    </Td>
                    <Td>
                      <Badge colorScheme="blue">
                        {subject.series?.name}
                      </Badge>
                    </Td>
                    <Td>
                      {subject.series?.type ? (
                        <Badge 
                          colorScheme={subject.series.type.toLowerCase().includes('hindi') ? 'orange' : 'green'}
                          size="sm"
                        >
                          {subject.series.type}
                        </Badge>
                      ) : (
                        <Text fontSize="sm" color="gray.500">Not set</Text>
                      )}
                    </Td>
                    <Td>
                      <Badge colorScheme="green">
                        {subject.class?.name}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<Text>✏️</Text>}
                          aria-label="Edit"
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          onClick={() => handleEdit(subject)}
                          isDisabled={loading}
                        />
                        <IconButton
                          icon={<Text>🗑️</Text>}
                          aria-label="Delete"
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleDelete(subject.id)}
                          isDisabled={loading}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingSubject ? 'Edit Subject' : 'Create New Subject'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Series</FormLabel>
                  <Select
                    value={formData.seriesId}
                    onChange={(e) => setFormData({ ...formData, seriesId: e.target.value })}
                    isDisabled={loading}
                  >
                    <option value="">Choose a series</option>
                    {series.map((item: any) => (
                      <option key={item.id} value={item.id}>
                        {item.name} 
                        {item.type && ` (${item.type})`}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Class</FormLabel>
                  <Select
                    value={formData.classId}
                    onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
                    isDisabled={loading}
                  >
                    <option value="">Choose a class</option>
                    {classes.map((item: any) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Subject Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., algebra"
                    isDisabled={loading}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe this subject..."
                    rows={3}
                    isDisabled={loading}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose} isDisabled={loading}>
                Cancel
              </Button>
              <Button type="submit" colorScheme="green" isLoading={loading}>
                {editingSubject ? 'Update' : 'Create'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default SubjectsPage; 